import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  createCompanyApiKey,
  deleteCompanyApiKey,
  listCompanyApiKeys,
  revealCompanyApiKey,
  updateCompanyApiKey,
} from "../services/companyApiKeyService";
import type {
  CreateCompanyApiKeyRequest,
  UpdateCompanyApiKeyRequest,
} from "../types/companyApiKey.types";

const listKey = (companyGuid: string | null) => ["companyApiKeys", companyGuid];

export const useCompanyApiKeys = (companyGuid: string | null) =>
  useQuery({
    queryKey: listKey(companyGuid),
    queryFn: () => listCompanyApiKeys(companyGuid!),
    enabled: !!companyGuid,
    staleTime: 5_000,
  });

export const useCreateCompanyApiKey = (companyGuid: string | null) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateCompanyApiKeyRequest) => createCompanyApiKey(req),
    onSuccess: () => {
      toast.success("API 키가 등록되었습니다.");
      qc.invalidateQueries({ queryKey: listKey(companyGuid) });
    },
    onError: (err: Error) => toast.error(`등록 실패: ${err.message}`),
  });
};

export const useUpdateCompanyApiKey = (companyGuid: string | null) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { idx: number; req: UpdateCompanyApiKeyRequest }) =>
      updateCompanyApiKey(args.idx, args.req),
    onSuccess: () => {
      toast.success("API 키가 수정되었습니다.");
      qc.invalidateQueries({ queryKey: listKey(companyGuid) });
    },
    onError: (err: Error) => toast.error(`수정 실패: ${err.message}`),
  });
};

export const useDeleteCompanyApiKey = (companyGuid: string | null) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (idx: number) => deleteCompanyApiKey(idx),
    onSuccess: () => {
      toast.success("API 키가 삭제되었습니다. 이제 공용 키로 폴백합니다.");
      qc.invalidateQueries({ queryKey: listKey(companyGuid) });
    },
    onError: (err: Error) => toast.error(`삭제 실패: ${err.message}`),
  });
};

/**
 * 평문 조회는 캐시하지 않는다 — 수정 다이얼로그를 열 때 그 시점 값만 받아온다.
 * useQuery 로 두면 비밀값이 쿼리 캐시에 남는다.
 */
export const useRevealCompanyApiKey = () =>
  useMutation({
    mutationFn: (idx: number) => revealCompanyApiKey(idx),
    onError: (err: Error) => toast.error(`키 조회 실패: ${err.message}`),
  });
